/**
 * Cross-Matter Changes API Service
 *
 * Provides:
 * - Paginated list query (for DataTable with server-side pagination)
 * - Detail query (for single-matter change view)
 *
 * Pattern follows Matters/Intakes DataTable approach.
 */

import { createApi } from '@reduxjs/toolkit/query/react';

import { PaginatedResponse, PaginationParams } from '../types/pagination-types';
import { baseQueryWithErrorHandling } from './baseQuery';
import type {
  BulkUpdateStatusAction,
  BulkUpdateStatusResponseSchema,
  ChangedCoverageEntity,
  ChangedProviderEntity,
  ChangedStaffNoteEntity,
  ChangedTaskEntity,
  ChangedTaskUpdateEntity,
  ChangedTreatmentRequestEntity,
  ChangeType,
  EntityCategory,
  EntityCategoryCounts,
  MatterChangeDetailSchema,
  MatterChangeSummarySchema,
} from './generated/crossMatterChangesService';

export type {
  ChangedCoverageEntity,
  ChangedProviderEntity,
  ChangedStaffNoteEntity,
  ChangedTaskEntity,
  ChangedTaskUpdateEntity,
  ChangedTreatmentRequestEntity,
  ChangeType,
  EntityCategory,
  EntityCategoryCounts,
  MatterChangeDetailSchema,
  MatterChangeSummarySchema,
};

export interface CrossMatterChangesFilterParams extends PaginationParams {
  firm?: string;
  matter_name?: string;
  matter_id?: string;
}

export interface CrossMatterChangesFilterOption {
  id: string;
  name: string;
}

export interface CrossMatterChangesFilterOptions {
  matters: CrossMatterChangesFilterOption[];
  firms: CrossMatterChangesFilterOption[];
}

export const crossMatterChangesApiClient = createApi({
  reducerPath: 'crossMatterChangesApi',
  baseQuery: baseQueryWithErrorHandling,
  tagTypes: ['CrossMatterChanges'],
  endpoints: (builder) => ({
    getCrossMatterChangesList: builder.query<
      PaginatedResponse<MatterChangeSummarySchema>,
      Partial<CrossMatterChangesFilterParams>
    >({
      query: (params) => ({
        url: 'api/matter-changes/',
        params,
      }),
      providesTags: ['CrossMatterChanges'],
    }),

    getCrossMatterChangeDetail: builder.query<
      MatterChangeDetailSchema,
      { matterId: string }
    >({
      query: ({ matterId }) => ({
        url: `api/matter-changes/${matterId}/`,
      }),
      providesTags: (_result, _error, { matterId }) => [
        { type: 'CrossMatterChanges', id: matterId },
      ],
    }),

    getCrossMatterChangesFilterOptions: builder.query<
      CrossMatterChangesFilterOptions,
      void
    >({
      query: () => ({
        url: 'api/matter-changes/filter-options/',
      }),
    }),

    bulkUpdateChangeStatus: builder.mutation<
      BulkUpdateStatusResponseSchema,
      { eventIds: string[]; action: BulkUpdateStatusAction }
    >({
      query: ({ eventIds, action }) => ({
        url: 'api/matter-changes/bulk-update-status/',
        method: 'POST',
        body: { event_ids: eventIds, action },
      }),
      invalidatesTags: ['CrossMatterChanges'],
    }),
  }),
});

export const {
  useGetCrossMatterChangesListQuery,
  useGetCrossMatterChangeDetailQuery,
  useGetCrossMatterChangesFilterOptionsQuery,
  useBulkUpdateChangeStatusMutation,
} = crossMatterChangesApiClient;
